import React, { useEffect, useRef, useState } from 'react';

import type {
  YieldShooterInstance,
  YieldShooterEventParams,
  YieldShooterState,
} from '../types/yieldShooter';
import type { GameResult } from './YieldShooterGame';
import { STUDIO_SHOOTER_CSS } from './studioShooterTheme';

export type { GameResult };

type Props = {
  /** Fired on each level pass/fail and on game completion. */
  onResult?: (result: GameResult) => void;
  /** Fired when the game's own "Quit" button is pressed. */
  onExit?: () => void;
};

type LoadState = 'loading' | 'ready' | 'error';

/* Studio-skinned twin of the Yield Shooter. It loads a SEPARATE, byte-identical
   copy of the vendored bundle (so the original game and this one never share an
   asset or interfere) and reskins it to the AnalysisStudioLive design language.

   The reskin is purely additive: the game's logic is untouched. Its visuals are
   restyled by `STUDIO_SHOOTER_CSS`, which is baked into the iframe document at
   creation time. Because the bundle ships zero `!important` rules and SVG colors
   are presentation attributes, these `!important` overrides win the cascade no
   matter when the game injects its own stylesheet — so there is no theme flash
   and no way for the game to clobber the skin. */
const APP_JS = `${process.env.PUBLIC_URL || ''}/puzzles/yield-shooter-studio/app.js`;

/* Inlined host document. Same same-origin srcdoc mechanism as the original host
   (deployment-proof: no `.html` asset for an SPA rewrite to hijack), but with a
   light studio surface and the studio skin embedded directly in <head> so the
   game renders in the studio palette from its very first paint. */
const HOST_DOC =
  '<!doctype html><html><head><meta charset="utf-8">' +
  '<style>html,body{margin:0;padding:0;width:100%;height:100%;overflow:hidden;' +
  'background:#f7f9fc}#container{position:absolute;inset:0}' +
  STUDIO_SHOOTER_CSS +
  '</style>' +
  `<script src="${APP_JS}"></` +
  'script></head><body><div id="container"></div></body></html>';

const RESULT_EVENTS = new Set([
  'levelPassed',
  'levelFailed',
  'gameCompleted',
  'gameFailed',
]);

const StudioYieldShooterGame: React.FC<Props> = ({ onResult, onExit }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const instanceRef = useRef<YieldShooterInstance | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');

  // Keep latest callbacks without rebinding the iframe.
  const onResultRef = useRef(onResult);
  const onExitRef = useRef(onExit);
  onResultRef.current = onResult;
  onExitRef.current = onExit;

  const handleLoad = () => {
    const frame = iframeRef.current;
    const win = frame?.contentWindow;
    const container = frame?.contentDocument?.getElementById('container');
    if (!win || !container) {
      setLoadState('error');
      return;
    }

    // The bundle is a synchronous <script> in the srcdoc <head>, so the global
    // is normally present at the load event — poll briefly for the 5MB parse.
    let tries = 0;
    const start = () => {
      if (!win.startYieldShooter) {
        if (tries++ > 60) setLoadState('error');
        else window.setTimeout(start, 50);
        return;
      }

      const instance = win.startYieldShooter({
        config: {
          game: {},
          lang: {
            defaultLang: 'en',
            availableDefinitions: [
              { code: 'en', name: 'English' },
              { code: 'ro', name: 'Română' },
            ],
          },
        },
        domContainer: container,
      });
      instanceRef.current = instance;
      setLoadState('ready');

      instance.hooks.gameEvent.tap(
        'pmweb',
        (_name: string, params: YieldShooterEventParams, state: YieldShooterState) => {
          const name = params?.name;
          if (name && RESULT_EVENTS.has(name)) {
            onResultRef.current?.({
              status: name as GameResult['status'],
              level: Number(state?.levelNumber ?? params?.level ?? 0),
              score: Number(state?.score ?? params?.score ?? 0),
            });
          }
        }
      );
      instance.hooks.requestExitGame.tap('pmweb', () => onExitRef.current?.());
    };

    start();
  };

  // Dispose runs before React removes the <iframe> (the iframe teardown then
  // disposes of everything else: document, styles, hotkeys).
  useEffect(
    () => () => {
      try {
        instanceRef.current?.dispose();
      } catch {
        /* iframe removal handles the rest */
      }
      instanceRef.current = null;
    },
    []
  );

  return (
    <div className="puzzles__game">
      {loadState === 'loading' && (
        <div className="puzzles__game-status">Loading game…</div>
      )}
      {loadState === 'error' && (
        <div className="puzzles__game-status">
          Couldn’t load the game bundle (
          <code>/puzzles/yield-shooter-studio/app.js</code>).
        </div>
      )}
      <iframe
        ref={iframeRef}
        className="puzzles__game-frame"
        title="Yield Shooter"
        srcDoc={HOST_DOC}
        onLoad={handleLoad}
      />
    </div>
  );
};

export default StudioYieldShooterGame;
